import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TeamLogo from "../../components/TeamLogo";
import PlayerNameCell from "../../components/PlayerNameCell";
import NbaTeamLegendsRow, {
  collectFranchiseLegendIds,
} from "../../components/nba/NbaTeamLegendsRow";
import { useGetNbaTeamPlayersQuery } from "../../store/api/nbaApi";
import { getNbaFranchise } from "../../utils/nbaFranchises";
import { QUERY_PAGE_SIZE_OPTIONS } from "../../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";
import { brandRgba, BRAND_COLORS } from "../../utils/brandAssets";

const LEGEND_ROW_CLASS = "nba-franchise-legend-row";

const NbaTeamDetails = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const franchiseId = String(teamId || "").toUpperCase();
  const franchise = getNbaFranchise(franchiseId);
  const { data, isLoading, isError } = useGetNbaTeamPlayersQuery(franchiseId, {
    skip: !franchise,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState([
    { field: "playerName", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const sortedField = sortModel[0]?.field;
  const legendIds = useMemo(
    () => collectFranchiseLegendIds(data?.leaders),
    [data?.leaders]
  );

  const players = useMemo(() => {
    const rows = data?.players || [];
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((player) => {
      const name = String(player.playerName || "").toLowerCase();
      const id = String(player.playerId || "").toLowerCase();
      return name.includes(needle) || id.includes(needle);
    });
  }, [data?.players, searchTerm]);

  const columns = useMemo(
    () => [
      {
        field: "playerName",
        headerName: "Player",
        flex: 1,
        minWidth: 220,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {legendIds.has(params.row.playerId) ? (
              <Typography
                component="span"
                aria-label="Franchise legend"
                sx={{ fontSize: "0.85rem", lineHeight: 1, flexShrink: 0 }}
              >
                ★
              </Typography>
            ) : null}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <PlayerNameCell
                playerId={params.row.playerId}
                name={params.value}
                sport="nba"
              />
            </Box>
          </Box>
        ),
      },
    ],
    [sortedField, legendIds]
  );

  if (!franchise) {
    return (
      <Box sx={{ py: 2, maxWidth: 960, mx: "auto" }}>
        <Alert severity="warning">Unknown NBA franchise.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/app/nba/teams")}
          sx={{ mt: 2 }}
        >
          All teams
        </Button>
      </Box>
    );
  }

  const aliasNames = (franchise.aliases || [])
    .map((alias) => alias.name)
    .filter((name, index, names) => name !== franchise.name && names.indexOf(name) === index);

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 1200, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/app/nba/teams")}
        sx={{ mb: 2 }}
      >
        All teams
      </Button>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <TeamLogo
          sport="nba"
          franchiseId={franchise.id}
          teamName={franchise.name}
          size={72}
        />
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {franchise.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {franchise.from} – {franchise.to}
            {franchise.titles
              ? ` · ${franchise.titles} championship${franchise.titles === 1 ? "" : "s"}`
              : ""}
          </Typography>
          {aliasNames.length > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Also: {aliasNames.join(", ")}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {isError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load the roster from player seasons.
        </Alert>
      ) : null}

      <Box sx={{ mb: 3 }}>
        <NbaTeamLegendsRow
          franchiseName={franchise.name}
          leaders={data?.leaders}
          isLoading={isLoading}
        />
      </Box>

      <TextField
        size="small"
        label="Search players"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        sx={{ mb: 2, minWidth: 240 }}
      />

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={players.map((player) => ({
            id: player.playerId,
            ...player,
          }))}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          disableColumnMenu
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowHeight={52}
          onRowClick={(params) =>
            navigate(`/app/nba/player/${params.row.playerId}`)
          }
          getRowClassName={(params) =>
            legendIds.has(params.id) ? LEGEND_ROW_CLASS : ""
          }
          sx={{
            ...getResultsGridSx(),
            cursor: "pointer",
            [`& .${LEGEND_ROW_CLASS}`]: {
              backgroundColor: brandRgba(BRAND_COLORS.cyan, 0.06),
            },
            [`& .${LEGEND_ROW_CLASS}:hover`]: {
              backgroundColor: brandRgba(BRAND_COLORS.cyan, 0.1),
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default NbaTeamDetails;
